import axios from "axios";
import {
    buildServiceParamMap,
    mergeById,
    sortByDate,
    RSENTS_URL,
    mergeByIdMaintainOrder,
    mergeByAtt, compareBy
} from "../../common";

import { userInfo } from "../../common/user-info";
import { authHeader } from "../../common/auth-header";
import { relativeTimeThreshold } from "moment";
import jwtInterceptor  from '../../common/jwt-interceptor';

const state = {
    likesByShareId: {},
    likeCountsByShareId: {},
    pendingShareIds: new Set(),
    fetchTimeout: null,
    batchFetchPromise: null
};

const getters = {
    likesByShareId: state => id => {
        console.log("==========")
        console.log(state.likesByShareId);
        console.log("==========")
        return state.likesByShareId[id];
    },
    likeCountsByShareId: state => id => {
        return state.likeCountsByShareId[id];
    }
};

const actions = {

    async likeContent({ commit, state }, inparams) {
        const { shareId, likeType } = inparams;
        const user = userInfo();
        const atts = {
            context_id: shareId,
            context_type: "Share",
            type: likeType,
            creator_id: user.user_id,
            include: 'creator_name'
        };
        const requestOptions = {
            method: "POST",
            url: RSENTS_URL(),
            headers: {
                "Content-Type": "application/json"
            },
            data: {
                data: {
                    type: "sentiments",
                    attributes: atts
                }
            }
        };

        const response = await jwtInterceptor(requestOptions);
        // Don't do optimistic update - just fetch the actual count
        // This prevents the increment issue when unliking
        this.dispatch("sentiments/fetchContentLikeCounts", { shareId });
    },

    async unLikeContent({ commit, state }, inparams) {
        const { shareId, likeId } = inparams;
        const user = userInfo();
        const atts = {
            like_id: likeId
        };
        const requestOptions = {
            method: "DELETE",
            url: RSENTS_URL() + "/" + likeId + ".json",
            headers: {
                "Content-Type": "application/json"
            },
            data: {
                data: {
                    type: "sentiments",
                    attributes: atts
                }
            }
        };

        const response = await jwtInterceptor(requestOptions);
        this.dispatch("sentiments/fetchContentLikeCounts", { shareId });
    },

    async fetchContentLikeCounts({ commit }, inparams) {
        const { shareId } = inparams;
        const user = userInfo();
        var jsonparams = {
            context_id: shareId,
            context_type: "Share",
            //like: likeType,
            "page[size]": 1
        };
        var params = buildServiceParamMap(jsonparams);
        const requestOptions = {
            method: "GET",
            headers: {
                "Content-Type": "application/json"
            },
            url: RSENTS_URL() + '/total',
            params
        };
        const response = await jwtInterceptor(requestOptions);
        var totalList = response.data.totals;

        if (shareId) {
            if (totalList.length == 0)
            {
                // --- Hack to set counts to 0 if there are no totals
                commit("setLikeCountsByShareId", {
                    id: shareId + 'Like',
                    count: 0
                });
                commit("setLikeCountsByShareId", {
                    id: shareId + 'Thank',
                    count: 0
                });
                commit("setLikeCountsByShareId", {
                    id: shareId + 'Love',
                    count: 0
                });
            }
            totalList.forEach(obj => {
                commit("setLikeCountsByShareId", {
                    id: shareId + obj.type,
                    count: obj.total
                });
            });
        }
    },

    // Add shareId to pending batch and schedule batch fetch
    queueShareIdForBatchFetch({ commit, state, dispatch }, { shareId }) {
        if (!shareId) return;

        // Convert to string for consistent storage
        const shareIdStr = String(shareId);
        commit("addPendingShareId", shareIdStr);

        // Clear existing timeout
        if (state.fetchTimeout) {
            clearTimeout(state.fetchTimeout);
        }

        // Set new timeout to batch requests
        const timeout = setTimeout(() => {
            dispatch("executeBatchFetch");
        }, 50); // Wait 50ms to collect more shareIds

        commit("setFetchTimeout", timeout);
    },

    async executeBatchFetch({ commit, state }) {
        const shareIds = Array.from(state.pendingShareIds);
        if (shareIds.length === 0) return;

        // Clear pending shareIds
        commit("clearPendingShareIds");

        // If there's already a batch fetch in progress, wait for it
        if (state.batchFetchPromise) {
            await state.batchFetchPromise;
        }

        const promise = this.dispatch("sentiments/fetchContentsLikeCounts", { shareIds });
        commit("setBatchFetchPromise", promise);

        try {
            await promise;
        } finally {
            commit("setBatchFetchPromise", null);
        }
    },

    async fetchContentsLikeCounts({ commit }, inparams) {
        const { shareIds } = inparams;
        if (!shareIds || shareIds.length === 0) return;

        // Convert shareIds to numbers
        const numericShareIds = shareIds.map(id => parseInt(id, 10)).filter(id => !isNaN(id));
        if (numericShareIds.length === 0) return;

        const user = userInfo();
        var jsonparams = {
            context_ids: numericShareIds.join(','), // Now passing numeric IDs
            context_type: "Share",
            "page[size]": 1,
            "page[limit]": 100
        };
        var params = buildServiceParamMap(jsonparams);
        const requestOptions = {
            method: "GET",
            headers: {
                "Content-Type": "application/json"
            },
            url: RSENTS_URL() + '/totals',
            params
        };

        try {
            const response = await jwtInterceptor(requestOptions);
            const totalsMap = response.data.totals;

            // Process the response for each shareId (using original shareIds to maintain string consistency)
            shareIds.forEach((shareId, index) => {
                // Use the numeric version for lookup in the response
                const numericShareId = numericShareIds[index];
                const shareIdTotals = totalsMap[numericShareId];

                if (!shareIdTotals || shareIdTotals.length === 0) {
                    // Set counts to 0 if there are no totals
                    ['Like', 'Thank', 'Love'].forEach(type => {
                        commit("setLikeCountsByShareId", {
                            id: shareId + type,
                            count: 0
                        });
                    });
                } else {
                    shareIdTotals.forEach(obj => {
                        commit("setLikeCountsByShareId", {
                            id: shareId + obj.type,
                            count: obj.total
                        });
                    });
                }
            });
        } catch (error) {
            console.error('Error fetching batch sentiment counts:', error);
            // Fallback to individual fetches on error
            for (const shareId of shareIds) {
                this.dispatch("sentiments/fetchContentLikeCounts", { shareId });
            }
        }
    },

    async fetchContentLikes({ commit }, inparams) {
        const { shareId, likeType } = inparams;
        const user = userInfo();
        var jsonparams = {
            context_id: shareId,
            context_type: "Share",
            type: likeType,
            include: 'creator_name',
            //like: likeType,
            "page[size]": 100
        };
        var params = buildServiceParamMap(jsonparams);
        const requestOptions = {
            method: "GET",
            headers: {
                "Content-Type": "application/json"
            },
            url: RSENTS_URL(),
            params
        };
        const response = await jwtInterceptor(requestOptions);
        var likes = response.data.data;
        console.log("LIKES!!!!!!!!!!!!!!!!!!!!!")
        console.log(likes)
        console.log('----------')
        console.log(response.data)
        var key = shareId
        commit("setAllLikesByShareId", {key, likes: likes});
    }
};

const mutations = {
    clearState: state => {
        state.likesByShareId = {};
        state.likeCountsByShareId = {};
        state.pendingShareIds = new Set();
        state.fetchTimeout = null;
        state.batchFetchPromise = null;
    },
    setAllLikesByShareId: (state, params) => {
        const {key, likes} = params
        var curarr = state.likesByShareId[key];
        if (!curarr) {
            curarr = [];
        }
        var theLikes = likes ? likes : []

        var byIdObj = {};
        byIdObj[key] = theLikes
        state.likesByShareId = { ...state.likesByShareId, ...byIdObj };
    },
    setLikeCountsByShareId: (state, like) => {
        var lc = {};
        lc[like.id] = like.count;
        state.likeCountsByShareId = { ...state.likeCountsByShareId, ...lc };
    },
    incrementLikesByShareId: (state, like) => {
        var lc = {};
        lc[like.id] = !state.likeCountsByShareId[like.id] ? 1 : state.likeCountsByShareId[like.id] + 1;
        state.likeCountsByShareId = { ...state.likeCountsByShareId, ...lc };
    },
    decrementLikesByShareId: (state, like) => {
        var lc = {};
        const currentCount = state.likeCountsByShareId[like.id] || 0;
        lc[like.id] = Math.max(0, currentCount - 1); // Ensure it doesn't go below 0
        state.likeCountsByShareId = { ...state.likeCountsByShareId, ...lc };
    },
    addPendingShareId: (state, shareId) => {
        state.pendingShareIds.add(shareId);
    },
    clearPendingShareIds: (state) => {
        state.pendingShareIds.clear();
    },
    setFetchTimeout: (state, timeout) => {
        state.fetchTimeout = timeout;
    },
    setBatchFetchPromise: (state, promise) => {
        state.batchFetchPromise = promise;
    }
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations
};